import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Paper, Link } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import styles from "../../entities/ResetPasswordPage/styles";

const RESEND_COOLDOWN = 30; // seconds — UX only; the real limit is server-side

// Landing screen for unverified accounts (post-register / login while
// isEmailVerified === false). Tells the user to check their inbox and lets
// them resend the verification email.
const VerifyEmail = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const email = localStorage.getItem("email") || "";

  // If the account is already verified there is nothing to do here.
  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      navigate("/");
      return;
    }
    if (localStorage.getItem("isEmailVerified") === "true") {
      navigate("/dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    setLoading(true);
    try {
      await AuthService.resendVerificationEmail(email);
      // Response is intentionally generic (rate-limited server-side).
      showAlert(
        "If an account exists for that email, a verification link is on its way. Please check your inbox.",
        "success"
      );
      setCooldown(RESEND_COOLDOWN);
    } catch (error) {
      showAlert(error?.message || "Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <Box sx={styles.main}>
      <Container sx={styles.root} maxWidth="sm">
        <Paper sx={styles.paper}>
          <Grid container sx={styles.gridContainer}>
            <Grid item xs={12} sx={styles.gridItem}>
              <Box sx={styles.formContain}>
                <Typography variant="h4" sx={styles.mainheading}>
                  Verify your email
                </Typography>
                <Box sx={styles.headingborder}></Box>

                <Typography sx={styles.descriptionText}>
                  We&apos;ve sent a verification link to{" "}
                  <strong>{email || "your email address"}</strong>. Click the
                  link in that email to activate your account. You won&apos;t be
                  able to access the dashboard until your email is verified.
                </Typography>

                <LoadingButton
                  loading={loading}
                  onClick={handleResend}
                  fullWidth
                  variant="contained"
                  disabled={cooldown > 0}
                  sx={styles.submit}
                >
                  {cooldown > 0
                    ? `Resend available in ${cooldown}s`
                    : "Resend verification email"}
                </LoadingButton>

                <Box sx={{ textAlign: "center", mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={handleLogout}
                    underline="hover"
                    sx={{ color: "#878787", fontSize: "13px" }}
                  >
                    Sign in with a different account
                  </Link>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmail;
